/*
 * Copyright (c) 2017 Datarista, Inc.
 */

/* eslint-disable consistent-return, no-unexpected-multiline */

import { polyfill } from 'es6-promise';
import { completeAction, handleError, startAction } from './common';
import { accountServiceClient } from '../utils/api';
import { compare } from '../utils/common';

polyfill();

export const actionTypes = {
  DATASET_VERSION_INFO_FETCH_SUCCESS: 'DATASET_VERSION_INFO_FETCH_SUCCESS',
  DATASET_VERSION_REPORT_FETCH_SUCCESS: 'DATASET_VERSION_REPORT_FETCH_SUCCESS',
  OPEN_REPORTS_PREVIEW_SUCCESS: 'OPEN_REPORTS_PREVIEW_SUCCESS',
  REPORT_LINK_FETCH_SUCCESS: 'REPORT_LINK_FETCH_SUCCESS',
  REPORT_FETCH_SUCCESS: 'REPORT_FETCH_SUCCESS',
  SET_DATASET_SUCCESS: 'SET_DATASET_SUCCESS',
  SET_SELECTED_VERSION_SUCCESS: 'SET_SELECTED_VERSION_SUCCESS',
  ACCOUNT_MATCH_REPORT_SUCCESS: 'ACCOUNT_MATCH_REPORT_SUCCESS',
  ORDER_MATCH_REPORT_SUCCESS: 'ORDER_MATCH_REPORT_SUCCESS',
  ORDER_MATCH_REPORT_SERIES_SUCCESS: 'ORDER_MATCH_REPORT_SERIES_SUCCESS',
  ORDER_SAVED_SEARCH_SUCCESS: 'ORDER_SAVED_SEARCH_SUCCESS',
  CLEAR_ORDER_MATCH_REPORT_SUCCESS: 'CLEAR_ORDER_MATCH_REPORT_SUCCESS',
  CUSTOMER_RECORD_METADATA_SUCCESS: 'CUSTOMER_RECORD_METADATA_SUCCESS',
  FIELD_GROUPS_AGG: 'FIELD_GROUPS_AGG',
  MATCHED_PAIR_REPORT_SUCCESS: 'MATCHED_PAIR_REPORT_SUCCESS',
  CLEAR_MATCHED_PAIR_REPORT_SUCCESS: 'CLEAR_MATCHED_PAIR_REPORT_SUCCESS',
};

// Connect to the API
const swaggerApi = accountServiceClient();

const receiveDataSetVersionInfo = datasetVersionInfo => ({
  type: actionTypes.DATASET_VERSION_INFO_FETCH_SUCCESS,
  datasetVersionInfo,
});

export function getDataSetVersionInfo(accountId, token) {
  const actionText = 'Getting Dataset Version Info';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getDataSetVersionInfos({
          Authorization: `bearer ${token}`,
          account_id: accountId,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const datasetVersionInfo = response.obj;
            dispatch(receiveDataSetVersionInfo(datasetVersionInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveDataSetVersionReport = datasetVersionReport => ({
  type: actionTypes.DATASET_VERSION_REPORT_FETCH_SUCCESS,
  datasetVersionReport,
});

export function getDataSetVersionReport(datasetId, version, token) {
  const actionText = 'Getting Dataset Version Reports';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getDataSetVersionReport({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          version,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const datasetVersionReport = response.obj;
            dispatch(receiveDataSetVersionReport(datasetVersionReport));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function getReportLink(datasetId, version, report, token) {
  const actionText = 'Getting Report Link';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getReportLink({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          version,
          report,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const reportLink = response.data.replace(/"/g, '');
            window.location = reportLink;
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveReport = reportData => ({
  type: actionTypes.REPORT_FETCH_SUCCESS,
  reportData,
});

export function getReport(datasetId, version, report, reportType, token) {
  const actionText = 'Getting Report To View';
  const formattedReportType = reportType.charAt(0).toUpperCase() + reportType.slice(1);
  const getReportMethod = `get${formattedReportType}Report`;
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report[getReportMethod]({
        Authorization: `bearer ${token}`,
        dataset_id: datasetId,
        version,
        report,
      })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const reportData = response.obj;
            dispatch(receiveReport(reportData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const setDatasetSuccess = datasetInfo => ({
  type: actionTypes.SET_DATASET_SUCCESS,
  datasetInfo,
});

const setVersionSuccess = version => ({
  type: actionTypes.SET_SELECTED_VERSION_SUCCESS,
  version,
});

export function getDataSetAndVersionReport(datasetId, token) {
  const actionText = 'Initializing Dataset, Selected Version, and Version Reports';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(async client => {
      try {
        // get datasets
        const dataResponse = await client.datasets.getDataSet({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
        });
        const selectedDataset = dataResponse.obj;
        completeAction(dispatch, 'Received Selected Dataset');

        // get dataset version
        const versionResponse = await client.datasets.getDataSetVersions({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
        });
        const datasetVersions = versionResponse.obj;
        const activeVersion = datasetVersions.filter(v => v.status === 1)[0].version;
        completeAction(dispatch, 'Receiving versions for selected dataset');

        // get reports
        const reportResponse = await client.report.getDataSetVersionReport({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          version: activeVersion,
        });
        const datasetVersionReport = reportResponse.obj;
        const datasetInfo = {
          id: selectedDataset.id,
          name: selectedDataset.name,
          selectedVersion: activeVersion,
          versions: datasetVersions.sort(compare),
        };
        completeAction(dispatch, actionText);

        dispatch(setDatasetSuccess(datasetInfo));
        dispatch(receiveDataSetVersionReport(datasetVersionReport));
      } catch (err) {
        handleError(err, dispatch, actionText);
      }
    });
  };
}

export function setSelectedVersion(version) {
  const actionText = 'Setting Selected Version for Dataset';
  return dispatch => {
    dispatch(setVersionSuccess(version));
    completeAction(dispatch, actionText);
  };
}

const receiveAccountMatchReport = accountMatchReportData => ({
  type: actionTypes.ACCOUNT_MATCH_REPORT_SUCCESS,
  accountMatchReportData,
});

export function getAccountMatchReport(token) {
  const actionText = 'Getting Account Match Report';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getAccountMatchReport({
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const reportData = response.obj;
            dispatch(receiveAccountMatchReport(reportData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveOrderMatchReport = orderMatchReportData => ({
  type: actionTypes.ORDER_MATCH_REPORT_SUCCESS,
  orderMatchReportData,
});

const receiveClearCustomerRecordMetadata = () => ({
  type: actionTypes.CLEAR_ORDER_MATCH_REPORT_SUCCESS,
});

export function getOrderMatchReport(token, orderId) {
  const actionText = 'Getting Order Match Report';
  return dispatch => {
    dispatch(receiveClearCustomerRecordMetadata());
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      function reportAttempt() {
          client.report
            .getOrderMatchReportAsync({
              Authorization: `bearer ${token}`,
              order_id: orderId,
            })
            .then(response => {
              if (response.status >= 200 && response.status < 300) {
                const reportData = response.obj;
                if (reportData.status === 'PROCESSING') {
                    // delay: server endpoint waits for 5 seconds, so this is only here in case that goes awry...
                    setTimeout(() => { reportAttempt(); }, 5000);
                } else {
                    dispatch(receiveOrderMatchReport(reportData));
                    completeAction(dispatch, actionText);
                }
              } else {
                return Promise.reject(response);
              }
            }
          ).catch(err => {
              handleError(err, dispatch, actionText);
          });
      }
      reportAttempt();
    });
  };
}

const receiveOrderMatchReportSeries = orderMatchReportSeriesData => ({
  type: actionTypes.ORDER_MATCH_REPORT_SERIES_SUCCESS,
  orderMatchReportSeriesData,
});

export function getOrderMatchReportSeries(token, orderId) {
  const actionText = 'Getting Order Match Series Report';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getOrderMatchReportSeries({
          Authorization: `bearer ${token}`,
          order_id: orderId,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const reportData = response.obj;
            dispatch(receiveOrderMatchReportSeries(reportData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveOrderSavedSearch = orderSavedSearchData => ({
  type: actionTypes.ORDER_SAVED_SEARCH_SUCCESS,
  orderSavedSearchData,
});

export function getOrderSavedSearch(token, orderId) {
  const actionText = 'Getting Order Saved Search';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getOrderSavedSearch({
          Authorization: `bearer ${token}`,
          order_id: orderId,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const data = response.obj;
            dispatch(receiveOrderSavedSearch(data));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveCustomerRecordMetadata = customerRecordMetadata => ({
  type: actionTypes.CUSTOMER_RECORD_METADATA_SUCCESS,
  customerRecordMetadata,
});

export function getCustomerRecordMetadata(token, orderId) {
  const actionText = 'Getting Customer Record Metadata';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getCustomerRecordMetadata({
          Authorization: `bearer ${token}`,
          order_id: orderId,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const data = response.obj;
            dispatch(receiveCustomerRecordMetadata(data));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveFieldGroupsAgg = fieldGroupsAgg => ({
  type: actionTypes.FIELD_GROUPS_AGG,
  fieldGroupsAgg,
});

export function getFieldGroupsAgg(token, orderId) {
  const actionText = 'Getting Field Groups Agg';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.report
        .getOrderFieldGroupsAggReport({
          Authorization: `bearer ${token}`,
          order_id: orderId,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const data = response.obj;
            dispatch(receiveFieldGroupsAgg(data));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}


const receiveMatchedPairsReport = matchedPairReportData => ({
  type: actionTypes.MATCHED_PAIR_REPORT_SUCCESS,
  matchedPairReportData,
});

const receiveClearMatchedPairsReport = () => ({
  type: actionTypes.CLEAR_MATCHED_PAIR_REPORT_SUCCESS,
});

export function getMatchedPairReport(token, orderId) {
  const actionText = 'Getting Matched Pairs Report';
  return dispatch => {
    dispatch(receiveClearMatchedPairsReport());
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      function reportAttempt() {
          client.report
            .getMatchedPairsReportAsync({
              Authorization: `bearer ${token}`,
              order_id: orderId,
            })
            .then(response => {
              if (response.status >= 200 && response.status < 300) {
                const reportData = response.obj;
                if (reportData.status === 'PROCESSING') {
                    // delay: server endpoint waits for 5 seconds, so this is only here in case that goes awry...
                    setTimeout(() => { reportAttempt(); }, 5000);
                } else {
                    dispatch(receiveMatchedPairsReport(reportData));
                    completeAction(dispatch, actionText);
                }
              } else {
                return Promise.reject(response);
              }
            }
          ).catch(err => {
              handleError(err, dispatch, actionText);
          });
      }
      reportAttempt();
    });
  };
}
